import React, { useState, useEffect } from 'react';

import { PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { ProjectDetails as ProjectDetailsType } from '../types/billing.ts';
import api from '../services/api.ts';

interface ProjectDetailsProps {
  projectId: string;
  onBack: () => void;
}

const ProjectDetails: React.FC<ProjectDetailsProps> = ({ projectId, onBack }) => {
  const [projectDetails, setProjectDetails] = useState<ProjectDetailsType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);

  useEffect(() => {
    loadProjectDetails();
  }, [projectId]);

  const loadProjectDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.billing.getProject(projectId);
      setProjectDetails(data);
    } catch (error) {
      console.error('Error loading project details:', error);
      setError('Failed to load project details. Please check if the API server is running.');
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    setRefreshing(true);
    await loadProjectDetails();
    setRefreshing(false);
  };

  const copyToClipboard = async (text2: string) => {
    if (!text2) return;

    // Modern API (HTTPS or localhost)
    if (navigator?.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text2);
        return;
      } catch (e) {
        console.warn("Clipboard API failed, falling back", e);
      }
    }

    // Fallback for HTTP / older browsers
    const textarea = document.createElement("textarea");
    textarea.value = text2;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();

    try {
      document.execCommand("copy");
    } finally {
      document.body.removeChild(textarea);
    }
  };

  const getServiceColorClass = (service: string, type: 'bg' | 'text') => {
    const colors = {
      kubernetes: type === 'bg' ? 'bg-blue-900' : 'text-blue-400',
      pinecone: type === 'bg' ? 'bg-green-900' : 'text-green-400',
      anthropic: type === 'bg' ? 'bg-yellow-900' : 'text-yellow-400',
      neon: type === 'bg' ? 'bg-purple-900' : 'text-purple-400',
    };
    return colors[service.toLowerCase() as keyof typeof colors] || (type === 'bg' ? 'bg-gray-900' : 'text-gray-400');
  };

  const handleCopy = async (key, value) => {
    await copyToClipboard(value);

    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 3000);
  };

  const CopyIcon = ({ className = "" }) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className={`w-4 h-4 ${className}`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2M10 10h8a2 2 0 012 2v8a2 2 0 01-2 2h-8a2 2 0 01-2-2v-8a2 2 0 012-2z"
      />
    </svg>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900 border border-red-700 rounded-lg p-6">
        <div className="flex items-center">
          <svg className="w-6 h-6 text-red-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <h3 className="text-lg font-medium text-red-400">Error Loading Project Details</h3>
            <p className="text-red-300 mt-1">{error}</p>
            <button
              onClick={loadProjectDetails}
              className="mt-3 px-4 py-2 bg-red-700 text-white rounded hover:bg-red-600 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!projectDetails) {
    return (
      <div className="text-center py-12">
        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-400">Project not found</h3>
        <p className="mt-1 text-sm text-gray-500">The requested project could not be found.</p>
        <button
          onClick={onBack}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-500 transition-colors"
        >
          Go Back
        </button>
      </div>
    );
  }

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

  // Prepare service data for charts
  const serviceData = Object.entries(projectDetails.cost_breakdown)
    .filter(([_, service]) => service.cost > 0)
    .map(([serviceName, service]) => ({
      name: serviceName,
      value: service.cost || 0
    }));

  const getServiceIcon = (service: string) => {
    switch (service.toLowerCase()) {
      case 'kubernetes':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
        );
      case 'pinecone':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
          </svg>
        );
      case 'anthropic':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        );
      case 'neon':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
          </svg>
        );
      default:
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start">
        <div className="mb-4 sm:mb-0">
          <button
            onClick={onBack}
            className="text-blue-400 hover:text-blue-300 mb-2 flex items-center transition-colors duration-200"
          >
            <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Projects
          </button>
          <h1 className="text-2xl sm:text-3xl font-bold text-white">{projectDetails.project.name}</h1>
          <p className="text-gray-400">ID: {projectDetails.project.id}</p>
          {projectDetails.project.user && (
            <div className="mt-2 flex items-center space-x-2">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span className="text-sm text-gray-300">{projectDetails.project.user.name}</span>
              <span className="text-sm text-gray-500">({projectDetails.project.user.email})</span>
            </div>
          )}
        </div>
        <div className="text-left sm:text-right">
          <p className="text-sm text-gray-400">Total Cost</p>
          <p className="text-2xl sm:text-3xl font-bold text-white">${projectDetails.total_cost.toFixed(2)}</p>
          <p className="text-sm text-gray-300 mt-1">{(projectDetails.total_credits ?? 0).toFixed(4)} credits</p>
          <div className="flex items-center space-x-2 mt-2">
            <button
              onClick={refreshData}
              disabled={refreshing}
              className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-500 transition-colors disabled:opacity-50"
            >
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </button>
            <span className={`inline-block px-2 py-1 rounded-full text-xs ${projectDetails.project.status === 'active' ? 'bg-green-900 text-green-400' : 'bg-red-900 text-red-400'
              }`}>
              {projectDetails.project.status}
            </span>
          </div>
        </div>
      </div>

      {/* Service Breakdown Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {Object.entries(projectDetails.cost_breakdown).map(([serviceName, service], index) => (
          <div key={serviceName} className="bg-gray-800 rounded-xl border border-gray-700 p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-white capitalize">{service.service_name}</h3>

              <div className={`p-2 rounded-lg ${getServiceColorClass(serviceName, 'bg')}`}>
                <div className={getServiceColorClass(serviceName, 'text')}>
                  {getServiceIcon(serviceName)}
                </div>
              </div>
            </div>
            <h4 className="text-lg font-medium text-white capitalize">{serviceName === 'hours' ? 'Compute Hours' : /*split service name by hyphen*/ serviceName.split('-')[1]}</h4><br />
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-400">Service Name</span>
                <span className="text-sm text-white font-medium">{service.service_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-400">Cost (USD)</span>
                <span className="text-sm text-white font-medium">${service.cost.toFixed(4)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-400">Cost (Credits)</span>
                <span className="text-sm text-white font-medium">{service.credits.toFixed(4)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-400">Records</span>
                <span className="text-sm text-white font-medium">{service.records}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-400">Total Units</span>
                <span className="text-sm text-white font-medium">{service.total_units.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-400">Unit Type</span>
                <span className="text-sm text-white font-medium">{service.unit_of_measure}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-400">Percentage</span>
                <span className="text-sm text-white font-medium">
                  {((service.cost / projectDetails.total_cost) * 100).toFixed(1)}%
                </span>
              </div>
              {/* New: Additional data display. Should only display first 10 characters and give user copy button incase they need more info. 
              This is the data in external_identifier and additional_data fields in the service response
              */}


              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-400">External Identifier</span>

                <div className="flex items-center gap-2">
                  <span className="text-sm text-white font-medium">
                    {service.external_identifier
                      ? String(service.external_identifier).slice(0, 10)
                      : "—"}
                    ...
                  </span>

                  {service.external_identifier && (
                    <button
                      onClick={() =>
                        handleCopy(
                          "external_identifier",
                          String(service.external_identifier)
                        )
                      }
                      className="text-gray-400 hover:text-white transition"
                      title="Copy"
                    >
                      <CopyIcon />
                    </button>
                  )}

                  {copiedKey === "external_identifier" && (
                    <span className="text-xs text-green-400">Copied</span>
                  )}
                </div>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-400">Additional Data</span>

                <div className="flex items-center gap-2">
                  <span className="text-sm text-white font-medium">
                    {/* Ensure additional data is a string if not, serialize as json */}
                    {service.additional_data ? JSON.stringify(service.additional_data).slice(0, 10) : "—"}
                    ...
                  </span>

                  {service.additional_data && (
                    <button
                      onClick={() =>
                        handleCopy(
                          "additional_data",
                          String(JSON.stringify(service.additional_data))
                        )
                      }
                      className="text-gray-400 hover:text-white transition"
                      title="Copy"
                    >
                      <CopyIcon />
                    </button>
                  )}

                  {copiedKey === "additional_data" && (
                    <span className="text-xs text-green-400">Copied</span>
                  )}
                </div>
              </div>


            </div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Cost Breakdown Pie Chart */}
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 sm:p-6">
          <h2 className="text-lg sm:text-xl font-semibold text-white mb-4">Cost Breakdown</h2>
          {serviceData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={serviceData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => `${name === 'hours' ? 'compute hours' : name}: $${value.toFixed(3)}`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {serviceData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: any) => [`$${Number(value).toFixed(4)}`, 'Cost']}
                  contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[300px] text-gray-400">
              No cost breakdown data available
            </div>
          )}
        </div>

        {/* Historical Trend */}
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 sm:p-6">
          <h2 className="text-lg sm:text-xl font-semibold text-white mb-4">Cost History</h2>
          {projectDetails.historical_data && projectDetails.historical_data.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={projectDetails.historical_data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="date" stroke="#9CA3AF" />
                <YAxis stroke="#9CA3AF" />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                  formatter={(value: any, name: string) => [
                    name === 'cost' ? `$${Number(value).toFixed(4)}` : value,
                    name === 'cost' ? 'Daily Cost' : 'Records'
                  ]}
                />
                <Line type="monotone" dataKey="cost" stroke="#3B82F6" strokeWidth={2} name="cost" />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[300px] text-gray-400">
              No historical data available
            </div>
          )}
        </div>
      </div>

      {/* Project Information */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 sm:p-6">
        <h2 className="text-lg sm:text-xl font-semibold text-white mb-4">Project Information</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <p className="text-sm text-gray-400">Last Updated</p>
            <p className="text-white">{new Date(projectDetails.updated_at).toLocaleString()}</p>
          </div>
          <div>
            <p className="text-sm text-gray-400">Project ID</p>
            <p className="text-white">{projectDetails.project.id}</p>
          </div>
          <div>
            <p className="text-sm text-gray-400">Status</p>
            <span className={`inline-block px-2 py-1 rounded-full text-xs ${projectDetails.project.status === 'active'
              ? 'bg-green-900 text-green-400'
              : 'bg-red-900 text-red-400'
              }`}>
              {projectDetails.project.status}
            </span>
          </div>
          <div>
            <p className="text-sm text-gray-400">Total Services</p>
            <p className="text-white">{Object.keys(projectDetails.cost_breakdown).length}</p>
          </div>
          <div>
            <p className="text-sm text-gray-400">Total Records</p>
            <p className="text-white">
              {Object.values(projectDetails.cost_breakdown).reduce((sum, service) => sum + service.records, 0)}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-400">Historical Data Points</p>
            <p className="text-white">{projectDetails.historical_data?.length || 0}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetails;